import React, { useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import ContentCopy from '@mui/icons-material/ContentCopy';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import PhoneAndroidOutlined from '@mui/icons-material/PhoneAndroidOutlined';
import { useNavigate } from 'react-router-dom';
import { usePenggunaAplikasi, DaftarPenggunaAplikasi } from '../rilis/PenggunaAplikasi';
import { getApiBaseUrl } from '../langganan/langgananUtil';

const fetcher = (url) => axios.get(url).then((r) => r.data.data);

const LOGO = {
  midtrans: { icon: <PaymentsOutlined sx={{ fontSize: 30 }} />, bg: '#e8f1fb', color: '#0b4a8b' },
  firebase: { icon: <NotificationsActiveOutlined sx={{ fontSize: 30 }} />, bg: '#fff4e5', color: '#b45309' },
  domain: { icon: <LanguageOutlined sx={{ fontSize: 30 }} />, bg: '#ecfdf5', color: '#047857' },
  aplikasi: { icon: <PhoneAndroidOutlined sx={{ fontSize: 30 }} />, bg: '#eef2ff', color: '#3730a3' },
};

const Logo = ({ k }) => {
  const l = LOGO[k] || { icon: <ExtensionOutlined sx={{ fontSize: 30 }} />, bg: '#f1f5f9', color: '#475569' };
  return (
    <Box sx={{ width: 56, height: 56, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: l.bg, color: l.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {l.icon}
    </Box>
  );
};

// Kartu integrasi seperti halaman "Integrations": logo, nama, deskripsi, kategori di bawah.
const KartuIntegrasi = ({ item, label, onClick }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{ p: 2.5, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%', '&:hover': { borderColor: 'primary.main', boxShadow: 1 } }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
      <Logo k={item.key} />
      {label}
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography fontWeight={700}>{item.nama}</Typography>
      <Typography variant="body2" color="text.secondary">{item.deskripsi}</Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">{item.kategori}</Typography>
  </Paper>
);

const formAwal = (item) => {
  const f = {};
  item.fields.forEach((fd) => { f[fd.key] = fd.tipe === 'boolean' ? !!item.config?.[fd.key] : (fd.rahasia ? '' : item.config?.[fd.key] || ''); });
  return f;
};

// Judul kelompok kunci (mis. Sandbox / Production); kelompok yang sedang dipakai ditandai.
// onHapus: tampilkan tombol hapus kunci kelompok ini (hanya bila kuncinya tersimpan).
const JudulGrup = ({ grup, form, onHapus }) => {
  const dipakai = grup === (form.isProduction ? 'Production' : 'Sandbox');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
      <Typography variant="subtitle2">{grup}</Typography>
      {dipakai && <Chip size="small" color="primary" label="Dipakai" />}
      {onHapus && (
        <Button size="small" color="error" onClick={() => onHapus(grup)} sx={{ ml: 'auto' }}>Hapus kunci {grup}</Button>
      )}
    </Box>
  );
};

// Konfirmasi sebelum menghapus kunci. target: { grup } (null = semua kunci).
const KonfirmasiHapus = ({ item, target, onBatal, onYa, saving }) => (
  <Dialog open={!!target} onClose={onBatal} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>
      {target?.grup ? `Hapus kunci ${target.grup}?` : `Hapus semua kunci ${item.nama}?`}
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        {target?.grup
          ? `Kunci ${target.grup} akan dihapus. Kunci lain tetap tersimpan.`
          : `Semua kunci ${item.nama} akan dihapus dan harus diisi ulang bila ingin dipakai lagi.`}
        {' '}Pembayaran yang masih menunggu tetap tercatat.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onBatal}>Batal</Button>
      <Button color="error" variant="contained" onClick={onYa} disabled={saving}>Hapus</Button>
    </DialogActions>
  </Dialog>
);

// Kelompok kunci yang sedang dipakai menurut config tersimpan (mis. Midtrans: Sandbox / Production).
const grupDipakai = (item) => (item.fields.some((f) => f.grup) ? (item.config?.isProduction ? 'Production' : 'Sandbox') : null);

// Isian kredensial. Field rahasia tidak pernah ditampilkan; kosong = tidak diubah.
// grupDikunci: kelompok kunci yang sedang dipakai sehingga tidak bisa dihapus (tombol hapusnya disembunyikan).
const FormKredensial = ({ item, form, setForm, onHapusGrup, grupDikunci }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
    {item.fields.map((f, i) => {
      const bisaHapus = onHapusGrup && (item.grupTersimpan || []).includes(f.grup) && f.grup !== grupDikunci;
      const judul = f.grup && f.grup !== item.fields[i - 1]?.grup
        ? <JudulGrup key={`g-${f.grup}`} grup={f.grup} form={form} onHapus={bisaHapus ? onHapusGrup : null} />
        : null;
      if (f.tipe === 'boolean') {
        return (
          <FormControlLabel
            key={f.key}
            control={<Switch checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />}
            label={f.label}
          />
        );
      }
      if (f.tipe === 'json') {
        const tersimpan = item.config?.[`${f.key}Tersimpan`];
        const unggah = (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          file.text().then((isi) => setForm({ ...form, [f.key]: isi }));
          e.target.value = '';
        };
        return (
          <Box key={f.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label={f.label}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              placeholder={tersimpan ? 'Tersimpan. Tempel JSON baru hanya bila ingin mengganti.' : 'Tempel isi file service account (.json)'}
              InputLabelProps={tersimpan ? { shrink: true } : undefined}
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              size="small"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            <Button component="label" size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }}>
              Unggah file .json
              <input type="file" accept=".json,application/json" hidden onChange={unggah} />
            </Button>
          </Box>
        );
      }
      const akhir = f.rahasia ? item.config?.[`${f.key}Akhir`] : null;
      return [judul,
        <TextField
          key={f.key}
          label={f.label}
          type={f.rahasia ? 'password' : 'text'}
          autoComplete="off"
          value={form[f.key]}
          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
          placeholder={akhir ? `Tersimpan (…${akhir})` : ''}
          helperText={akhir ? 'Kosongkan bila tidak ingin mengubah.' : ''}
          InputLabelProps={akhir ? { shrink: true } : undefined}
          fullWidth
          size="small"
        />,
      ];
    })}
  </Box>
);

// Satu URL yang perlu dipasang di dashboard layanan, dengan tombol salin.
const UrlSalin = ({ label, alamat, keterangan, tautan }) => {
  const [disalin, setDisalin] = useState(false);
  const salin = () => {
    navigator.clipboard?.writeText(alamat).then(() => {
      setDisalin(true);
      setTimeout(() => setDisalin(false), 2000);
    }).catch(() => {});
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" fontWeight={600}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
        <TextField value={alamat || 'Belum diatur (Konfigurasi > Alamat Web Menu)'} size="small" fullWidth InputProps={{ readOnly: true }} inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }} />
        <Button size="small" variant="outlined" startIcon={<ContentCopy fontSize="small" />} onClick={salin} disabled={!alamat} sx={{ flexShrink: 0 }}>
          {disalin ? 'Disalin' : 'Salin'}
        </Button>
      </Box>
      {keterangan && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{keterangan}</Typography>}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {(tautan || []).map((t) => (
          <Link key={t.url} href={t.url} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {t.label} <OpenInNew sx={{ fontSize: 12 }} />
          </Link>
        ))}
      </Box>
    </Box>
  );
};

// Panduan: tautan dashboard layanan, langkah mengambil kunci, dan URL yang dipasang di dashboard tersebut.
const Panduan = ({ panduan }) => {
  if (!panduan) return null;
  const daftarUrl = (panduan.url || []).map((u) => ({ ...u, alamat: u.basis === 'backend' ? `${getApiBaseUrl()}${u.path}` : u.alamat }));
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {(panduan.tautan || []).map((t) => (
          <Link key={t.url} href={t.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {t.label} <OpenInNew sx={{ fontSize: 14 }} />
          </Link>
        ))}
      </Box>
      {panduan.langkah && <Typography variant="caption" color="text.secondary">{panduan.langkah}</Typography>}
      {daftarUrl.map((u) => <UrlSalin key={u.label} {...u} />)}
    </Box>
  );
};

const pesanError = (err) => err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.';

const waktuLokal = (t) => (t ? new Date(t).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '-');

// Mode darurat Firebase (cadangan saat server mati): status salinan ke Firestore + tombol salin sekarang.
const StatusDarurat = () => {
  const url = `${getApiBaseUrl()}/darurat/status`;
  const { data, error, isLoading, mutate } = useSWR(url, (u) => axios.get(u).then((r) => r.data));
  const [menyalin, setMenyalin] = useState(false);
  const [pesan, setPesan] = useState(null);
  const salin = async () => {
    setMenyalin(true);
    setPesan(null);
    try {
      const r = await axios.post(`${getApiBaseUrl()}/darurat/salin`);
      setPesan(r.data?.dilewati ? { s: 'warning', t: 'Salinan dilewati: mode darurat tidak aktif.' }
        : { s: 'success', t: `Selesai: ${r.data.ditulis} dokumen diperbarui, ${r.data.dihapus} dihapus.` });
      mutate();
    } catch (e) {
      setPesan({ s: 'error', t: pesanError(e) });
    } finally {
      setMenyalin(false);
    }
  };
  if (isLoading) return <Box sx={{ py: 1 }}><CircularProgress size={20} /></Box>;
  if (error || !data) return <Alert severity="warning">Status mode darurat tidak bisa dimuat.</Alert>;
  const pt = data.putaranTerakhir;
  const m = data.menunggu || {};
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Mode darurat (cadangan saat server mati)</Typography>
      {data.aktif
        ? <Alert severity="success" sx={{ mb: 1.5 }}>Aktif. Data penting disalin ke Firestore tiap {data.intervalMenit} menit (project {data.projectIntegrasi}).</Alert>
        : <Alert severity="warning" sx={{ mb: 1.5 }}>Tidak berjalan. {data.alasan}</Alert>}
      <Table size="small">
        <TableBody>
          {data.projectEnv && (
            <TableRow><TableCell>Pengaman project di server (opsional)</TableCell><TableCell align="right">{data.projectEnv}</TableCell></TableRow>
          )}
          <TableRow><TableCell>Project di Integrasi</TableCell><TableCell align="right">{data.projectIntegrasi || '-'}</TableCell></TableRow>
          <TableRow><TableCell>Dokumen tersalin</TableCell><TableCell align="right">{data.dokumen}</TableCell></TableRow>
          <TableRow><TableCell>Terakhir ada perubahan disalin</TableCell><TableCell align="right">{waktuLokal(data.terakhirTulis)}</TableCell></TableRow>
          <TableRow><TableCell>Pemeriksaan terakhir</TableCell><TableCell align="right">
            {pt ? `${waktuLokal(pt.waktu)}${pt.galat ? ' (gagal)' : ''}` : 'belum sejak server dinyalakan'}
          </TableCell></TableRow>
          {data.aktif && (
            <TableRow><TableCell>Data darurat belum masuk server</TableCell><TableCell align="right">
              {m.transaksi || 0} transaksi, {m.absen || 0} absen{m.gagal ? `, ${m.gagal} gagal` : ''}
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      {pt?.galat && <Alert severity="error" sx={{ mt: 1.5 }}>Error terakhir: {pt.galat}</Alert>}
      {m.gagal > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Ada data darurat yang gagal masuk server dan dicoba ulang tiap jam. Alasannya terlihat di Dashboard Darurat (aplikasi, login admin/superadmin).
        </Alert>
      )}
      {pesan && <Alert severity={pesan.s} sx={{ mt: 1.5 }}>{pesan.t}</Alert>}
      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={salin} disabled={menyalin || !data.aktif}>
          {menyalin ? 'Menyalin...' : 'Salin sekarang'}
        </Button>
        <Button size="small" onClick={() => mutate()} disabled={menyalin}>Muat ulang status</Button>
      </Box>
    </Paper>
  );
};

const STATUS_CABANG = {
  terhubung: { label: 'Terhubung', color: 'success', variant: 'filled' },
  nonaktif: { label: 'Nonaktif', color: 'warning', variant: 'outlined' },
  belum: { label: 'Belum terhubung', color: 'default', variant: 'outlined' },
};

// Superadmin: pilih mode Default (akun pusat, tidak tampil di cabang) atau Per Cabang, dan lihat cabang yang sudah terhubung.
const DialogSuperadmin = ({ item, onClose, onSaved }) => {
  const [aktif, setAktif] = useState(item.aktif);
  const [mode, setMode] = useState(item.mode);
  const [form, setForm] = useState(() => formAwal(item));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hapus, setHapus] = useState(null);

  const terhubung = item.cabang.filter((c) => c.terhubung).length;
  const adaKunci = (item.grupTersimpan || []).length > 0;
  // Akun pusat dipakai transaksi bila integrasi aktif dan mode Default: kunci yang dipakai tidak bisa dihapus.
  const pusatDipakai = item.aktif && item.mode === 'default';

  const hapusKunci = async () => {
    setSaving(true);
    setError(null);
    try {
      const q = hapus.grup ? `?grup=${encodeURIComponent(hapus.grup)}` : '';
      await axios.delete(`${getApiBaseUrl()}/integrasi/${item.key}/kunci${q}`);
      onSaved(hapus.grup ? `Kunci ${hapus.grup} dihapus.` : `Semua kunci ${item.nama} dihapus.`);
    } catch (err) {
      setError(pesanError(err));
      setHapus(null);
    } finally {
      setSaving(false);
    }
  };
  const adaIsian = item.fields.some((f) => f.tipe !== 'boolean' && (f.rahasia ? String(form[f.key] || '').trim() !== '' : form[f.key] !== (item.config?.[f.key] || '')))
    || item.fields.some((f) => f.tipe === 'boolean' && !!form[f.key] !== !!item.config?.[f.key]);

  const simpan = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(`${getApiBaseUrl()}/integrasi/${item.key}`, { aktif, mode, ...(mode === 'default' && adaIsian ? { config: form } : {}) });
      onSaved(`${item.nama} disimpan.`);
    } catch (err) {
      setError(pesanError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{item.nama}</DialogTitle>
      <DialogContent dividers>
        <FormControlLabel
          control={<Switch checked={aktif} onChange={(e) => setAktif(e.target.checked)} />}
          label={<Typography fontWeight={600}>{aktif ? 'Aktif' : 'Nonaktif'}</Typography>}
          sx={{ mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
          {aktif
            ? `${item.nama} dipakai di semua cabang.`
            : `${item.nama} dimatikan untuk semua cabang. Kunci tetap tersimpan, aktifkan lagi kapan saja.`}
        </Typography>
        {item.keterangan && <Alert severity="success" sx={{ mb: 2 }}>Terhubung: {item.keterangan}</Alert>}
        {item.perCabang ? (<>
        <Typography variant="subtitle2" gutterBottom>Pengaturan akun</Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="default" control={<Radio />} label={
            <Box><Typography variant="body2" fontWeight={600}>Default</Typography>
              <Typography variant="caption" color="text.secondary">Semua cabang memakai akun pusat. Tidak tampil di admin cabang.</Typography></Box>
          } sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.5 } }} />
          <FormControlLabel value="per_cabang" control={<Radio />} label={
            <Box><Typography variant="body2" fontWeight={600}>Per Cabang</Typography>
              <Typography variant="caption" color="text.secondary">Tiap admin cabang menghubungkan akunnya sendiri. Cabang yang belum terhubung tidak bisa menerima pembayaran lewat {item.nama}.</Typography></Box>
          } sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }} />
        </RadioGroup>
        </>) : (
          <Typography variant="body2" color="text.secondary">Satu akun pusat dipakai semua cabang.</Typography>
        )}

        {mode === 'default' ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Akun pusat</Typography>
            <FormKredensial
              item={item}
              form={form}
              setForm={setForm}
              onHapusGrup={(grup) => setHapus({ grup })}
              grupDikunci={pusatDipakai ? grupDipakai(item) : null}
            />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Cabang terhubung: {terhubung} dari {item.cabang.length}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cabang</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {item.cabang.map((c) => (
                  <TableRow key={c.uuid}>
                    <TableCell>{c.namacabang}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" {...(STATUS_CABANG[c.status] || STATUS_CABANG.belum)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {item.key === 'firebase' && <Box sx={{ mt: 2 }}><StatusDarurat /></Box>}
        {/* Panduan (tautan dashboard & URL yang dipasang) di bagian paling bawah. */}
        <Box sx={{ mt: 2 }}><Panduan panduan={item.panduan} /></Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {mode === 'default' && adaKunci && !pusatDipakai && (
          <Button color="error" onClick={() => setHapus({ grup: null })} disabled={saving} sx={{ mr: 'auto' }}>Hapus semua kunci</Button>
        )}
        <Button onClick={onClose}>Batal</Button>
        <Button variant="contained" onClick={simpan} disabled={saving}>Simpan</Button>
      </DialogActions>
      <KonfirmasiHapus item={item} target={hapus} onBatal={() => setHapus(null)} onYa={hapusKunci} saving={saving} />
    </Dialog>
  );
};

// Admin cabang: hubungkan akun cabang sendiri, nonaktifkan sementara, atau hapus kunci.
const DialogCabang = ({ item, onClose, onSaved }) => {
  const [form, setForm] = useState(() => formAwal(item));
  const [aktif, setAktif] = useState(item.aktif !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hapus, setHapus] = useState(null);
  const url = `${getApiBaseUrl()}/integrasi-cabang/${item.key}`;

  const kirim = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(url, { config: form, aktif });
      onSaved(res.data?.message || `${item.nama} disimpan.`);
    } catch (err) {
      setError(pesanError(err));
    } finally {
      setSaving(false);
    }
  };

  const hapusKunci = async () => {
    setSaving(true);
    setError(null);
    try {
      const q = hapus.grup ? `?grup=${encodeURIComponent(hapus.grup)}` : '';
      const res = await axios.delete(`${url}${q}`);
      onSaved(res.data?.message || 'Kunci dihapus.');
    } catch (err) {
      setError(pesanError(err));
      setHapus(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{item.nama}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{item.deskripsi}</Typography>
        {item.terhubung && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={aktif} onChange={(e) => setAktif(e.target.checked)} />}
              label={<Typography fontWeight={600}>{aktif ? 'Aktif untuk cabang ini' : 'Nonaktif untuk cabang ini'}</Typography>}
            />
            <Typography variant="caption" color="text.secondary" component="div">
              {aktif ? `Pembayaran lewat ${item.nama} bisa dipakai di cabang ini.` : `${item.nama} dimatikan sementara. Kunci tetap tersimpan.`}
            </Typography>
          </Box>
        )}
        <FormKredensial
          item={item}
          form={form}
          setForm={setForm}
          onHapusGrup={(grup) => setHapus({ grup })}
          grupDikunci={item.terhubung && item.aktif !== false ? grupDipakai(item) : null}
        />
        {/* Panduan (tautan dashboard & URL yang dipasang) di bagian paling bawah. */}
        <Box sx={{ mt: 2 }}><Panduan panduan={item.panduan} /></Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {(item.grupTersimpan || []).length > 0 && (
          <Button color="error" onClick={() => setHapus({ grup: null })} disabled={saving} sx={{ mr: 'auto' }}>Hapus semua kunci</Button>
        )}
        <Button onClick={onClose}>Batal</Button>
        <Button variant="contained" onClick={kirim} disabled={saving}>{item.terhubung ? 'Simpan' : 'Hubungkan'}</Button>
      </DialogActions>
      <KonfirmasiHapus item={item} target={hapus} onBatal={() => setHapus(null)} onYa={hapusKunci} saving={saving} />
    </Dialog>
  );
};

// Superadmin: semua subdomain di bawah satu wildcard DNS *.domain — sistem (app, menu, backend, ...)
// dan tiap cabang (slug). Nama subdomain sistem tidak bisa dipakai cabang.
const DialogSubdomain = ({ data, onClose }) => {
  const domain = data?.domain;
  const alamat = (nama) => `${nama}.${domain || '<domain>'}`;
  const sistem = (data?.sistem || []).filter((s) => s.fungsi !== 'Dicadangkan');
  // Dicadangkan dikelompokkan per rencana pemakaian (mis. mail, webmail, smtp -> Email dari hosting).
  const cadangan = Object.values((data?.sistem || []).filter((s) => s.fungsi === 'Dicadangkan').reduce((acc, s) => {
    const k = s.rencana || 'Dicadangkan';
    (acc[k] ||= { rencana: k, nama: [] }).nama.push(s.nama);
    return acc;
  }, {}));
  const cabang = data?.cabang || [];
  const bentrok = cabang.filter((c) => c.bentrok);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Domain &amp; Subdomain</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cukup satu wildcard DNS <b>*.{domain || '<domain>'}</b> ke server. Domain diambil dari Alamat Web Menu
          (Konfigurasi){data?.alamatWebMenu ? `: ${data.alamatWebMenu}` : ' — belum diatur'}.
        </Typography>
        {!domain && <Alert severity="info" sx={{ mb: 2 }}>Atur Alamat Web Menu (mis. https://menu.juragankuliner.com) agar alamat lengkap tampil.</Alert>}
        {bentrok.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Slug cabang berikut memakai nama subdomain sistem, ganti di menu Cabang: {bentrok.map((c) => `${c.namacabang} (${c.slug})`).join(', ')}
          </Alert>
        )}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Subdomain yang dipakai</Typography>
        <Table size="small" sx={{ mb: 1 }}>
          <TableBody>
            {sistem.map((s) => (
              <TableRow key={s.nama}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{alamat(s.nama)}</TableCell>
                <TableCell>{s.fungsi}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Dicadangkan (belum dipakai)</Typography>
        <Table size="small" sx={{ mb: 1 }}>
          <TableBody>
            {cadangan.map((k) => (
              <TableRow key={k.rencana}>
                <TableCell sx={{ fontWeight: 600 }}>{k.nama.join(', ')}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{k.rencana}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2.5 }}>
          Semua nama di atas (dipakai maupun dicadangkan) tidak bisa dipakai sebagai nama/slug cabang.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Subdomain cabang ({cabang.length}) — halaman cabang</Typography>
        <Table size="small">
          <TableBody>
            {cabang.map((c) => (
              <TableRow key={c.uuid}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: c.bentrok ? 'error.main' : 'inherit' }}>{c.slug ? alamat(c.slug) : '-'}</TableCell>
                <TableCell>{c.namacabang}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Slug cabang diubah di menu Pengaturan › Cabang. QR meja tetap memakai alamat menu ({alamat('menu')}/menu/…).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
};

const Integrasi = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const base = getApiBaseUrl();
  const { data, error, isLoading, mutate } = useSWR(
    user ? `${base}/${superadmin ? 'integrasi' : 'integrasi-cabang'}` : null,
    fetcher,
    { refreshInterval: superadmin ? 0 : 60000, revalidateOnFocus: true }
  );
  const [aktif, setAktif] = useState(null);
  const [notif, setNotif] = useState(null);
  const [bukaSubdomain, setBukaSubdomain] = useState(false);
  const [bukaAplikasi, setBukaAplikasi] = useState(false);
  const pengguna = usePenggunaAplikasi(superadmin);
  const navigate = useNavigate();
  const akunAplikasi = (pengguna.data?.data || []).reduce((a, c) => a + c.akun.length, 0);
  const perluUpdate = (pengguna.data?.data || []).reduce((a, c) => a + c.akun.filter((x) => x.terbaru === false).length, 0);
  const { data: subdomain } = useSWR(superadmin ? `${base}/subdomain-terpakai` : null, (u) => axios.get(u).then((r) => r.data));

  const tersimpan = (msg) => {
    setAktif(null);
    setNotif(msg);
    mutate();
  };

  // Label mode hanya untuk superadmin; admin cabang hanya melihat status koneksinya sendiri.
  const label = (item) => {
    if (superadmin) {
      const perCabang = item.mode === 'per_cabang';
      const n = item.cabang.filter((c) => c.terhubung).length;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          {!item.aktif && <Chip size="small" label="Nonaktif" color="error" variant="outlined" />}
          <Chip size="small" label={perCabang ? 'Per Cabang' : 'Default'} color={perCabang ? 'primary' : 'default'} />
          {perCabang && <Typography variant="caption" color="text.secondary">{n}/{item.cabang.length} cabang terhubung</Typography>}
          {!perCabang && !item.terhubung && <Typography variant="caption" color="error">Akun pusat belum diatur</Typography>}
        </Box>
      );
    }
    if (item.terhubung && item.aktif === false) return <Chip size="small" label="Nonaktif" color="warning" variant="outlined" />;
    return item.terhubung
      ? <Chip size="small" label="Terhubung" color="success" />
      : <Chip size="small" label="Belum terhubung" variant="outlined" />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Integrasi</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {superadmin
          ? 'Atur akun layanan pihak ketiga. Pilih Default untuk memakai akun pusat di semua cabang, atau Per Cabang agar tiap cabang menghubungkan akunnya sendiri.'
          : 'Hubungkan akun layanan pihak ketiga milik cabang Anda.'}
      </Typography>

      {isLoading && <CircularProgress size={28} />}
      {error && <Alert severity="error">{pesanError(error)}</Alert>}
      {data && data.length === 0 && (
        <Alert severity="info">Belum ada integrasi yang perlu diatur oleh cabang.</Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        {(data || []).map((item) => (
          <KartuIntegrasi key={item.key} item={item} label={label(item)} onClick={() => setAktif(item)} />
        ))}
        {superadmin && (
          <KartuIntegrasi
            item={{
              key: 'domain',
              nama: 'Domain & Subdomain',
              deskripsi: `Wildcard *.${subdomain?.domain || 'domain'}: app, menu, backend, dan subdomain tiap cabang.`,
              kategori: 'Domain',
            }}
            label={(
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Chip size="small" label={`${subdomain?.cabang?.length ?? '-'} cabang`} />
                {subdomain?.cabang?.some((c) => c.bentrok) && <Chip size="small" color="warning" label="Ada bentrok" />}
              </Box>
            )}
            onClick={() => setBukaSubdomain(true)}
          />
        )}
        {superadmin && (
          <KartuIntegrasi
            item={{
              key: 'aplikasi',
              nama: 'Aplikasi Juragan POS',
              deskripsi: 'Versi aplikasi yang dipakai tiap akun per cabang, dan rilis/update aplikasi.',
              kategori: 'Aplikasi',
            }}
            label={(
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Chip size="small" label={`${akunAplikasi} akun`} />
                {perluUpdate > 0 && <Chip size="small" color="warning" label={`${perluUpdate} perlu update`} />}
              </Box>
            )}
            onClick={() => setBukaAplikasi(true)}
          />
        )}
      </Box>
      {bukaSubdomain && <DialogSubdomain data={subdomain} onClose={() => setBukaSubdomain(false)} />}
      {bukaAplikasi && (
        <Dialog open onClose={() => setBukaAplikasi(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 700 }}>Aplikasi Juragan POS</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Akun yang memakai aplikasi per cabang beserta versinya. Akun yang hanya memakai web tidak tampil.
            </Typography>
            <DaftarPenggunaAplikasi {...pengguna} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => navigate('/rilis-aplikasi')}>Kelola rilis</Button>
            <Button onClick={() => setBukaAplikasi(false)}>Tutup</Button>
          </DialogActions>
        </Dialog>
      )}

      {aktif && (superadmin
        ? <DialogSuperadmin key={aktif.key} item={aktif} onClose={() => setAktif(null)} onSaved={tersimpan} />
        : <DialogCabang key={aktif.key} item={aktif} onClose={() => setAktif(null)} onSaved={tersimpan} />)}

      <Snackbar open={!!notif} autoHideDuration={3000} onClose={() => setNotif(null)} message={notif} />
    </Box>
  );
};

export default Integrasi;
